import { create } from 'zustand';
import type { Feature, MultiPolygon, Polygon } from 'geojson';
import { ServiceArea } from '../types';
import { serviceAreasApi } from '../api/serviceAreas';
import { createPrefixedLogger } from '../utils/logger';
import { pointInPolygon } from '../utils/geo';

const log = createPrefixedLogger('store:serviceAreas');

// Feature flag. The polygon plumbing (store cache, loader, findContainingPoint,
// seed script, static GeoJSON files, web map render) is wired up but gated off
// because many seeded structures fall outside the OSM admin boundaries we
// fetched — using polygons for containment would mislabel those structures as
// out-of-area. Flip to `true` once structure-to-polygon alignment is fixed
// (or the polygon source is swapped for something that covers them). See
// memory: project_service_area_polygons_disabled.
export const POLYGON_RENDERING_ENABLED = false;

type ServiceAreaPolygon = Feature<Polygon | MultiPolygon>;

// Bootstrap fallback used during cold start before the first Firestore fetch
// returns. Matches the seeded docs so the geofence answer is the same either
// way during the race window. Keep this in sync with the seed script
// (scripts/seed-service-areas.ts) so a brand-new install is never stricter or
// laxer than a hydrated one.
const BOOTSTRAP_AREAS: ServiceArea[] = [
  {
    id: 'big-bear-lake',
    name: 'Big Bear Lake',
    active: true,
    bounds: { minLat: 34.2, maxLat: 34.3, minLng: -117.0, maxLng: -116.8 },
    center: { latitude: 34.2439, longitude: -116.9114, zoom: 11 },
    // Synthetic timestamps — only ever used until Firestore wins.
    createdAt: { seconds: 0, nanoseconds: 0 } as ServiceArea['createdAt'],
  },
  {
    id: 'leon-county',
    name: 'Leon County',
    active: true,
    bounds: { minLat: 30.25, maxLat: 30.75, minLng: -84.8, maxLng: -83.95 },
    center: { latitude: 30.4383, longitude: -84.2807, zoom: 11 },
    createdAt: { seconds: 0, nanoseconds: 0 } as ServiceArea['createdAt'],
  },
  {
    id: 'san-francisco',
    name: 'San Francisco',
    active: true,
    bounds: { minLat: 37.7, maxLat: 37.84, minLng: -123.02, maxLng: -122.35 },
    center: { latitude: 37.7749, longitude: -122.4194, zoom: 12 },
    createdAt: { seconds: 0, nanoseconds: 0 } as ServiceArea['createdAt'],
  },
];

interface ServiceAreasState {
  areas: ServiceArea[];
  isLoaded: boolean;
  polygonsByAreaId: Record<string, ServiceAreaPolygon>;
  polygonLoadFailures: Set<string>;
  initialize: () => Promise<void>;
  refresh: () => Promise<void>;
  /**
   * Find the first active service area containing the point, if any.
   * Synchronous so it can be called from sync UI paths; reads from the
   * already-cached `areas` array.
   */
  findContaining: (latitude: number, longitude: number) => ServiceArea | null;
  /**
   * Same as findContaining but consults the cached polygon for a precise
   * inside/outside answer when available. Falls back to the bounding-box
   * rectangle when the polygon hasn't been loaded yet so the UI doesn't
   * misfire during the fetch window.
   */
  findContainingPoint: (latitude: number, longitude: number) => ServiceArea | null;
  /**
   * Return every active service area whose bounds overlap the given viewport
   * box (AABB intersection). Used by the map to decide which areas to fetch
   * structures for — "the viewport shows any part of BBL → load BBL".
   */
  findIntersecting: (south: number, west: number, north: number, east: number) => ServiceArea[];
  /**
   * Fetch polygons for the given area ids if they aren't cached yet. Memoizes
   * 404s (area has no polygon file) but leaves transient errors retriable.
   */
  ensurePolygonsLoaded: (areaIds: string[]) => Promise<void>;
}

export const useServiceAreasStore = create<ServiceAreasState>((set, get) => ({
  areas: BOOTSTRAP_AREAS,
  isLoaded: false,
  polygonsByAreaId: {},
  polygonLoadFailures: new Set<string>(),

  initialize: async () => {
    if (get().isLoaded) return;
    await get().refresh();
  },

  refresh: async () => {
    try {
      const fresh = await serviceAreasApi.getActive();
      // Only swap the bootstrap fallback once we have at least one doc back —
      // an empty fetch likely means a bad rules deploy or an admin oopsie.
      // Better to keep serving the hardcoded value than lock everyone out.
      if (fresh.length === 0) {
        log.warn('Firestore returned 0 active service areas — keeping bootstrap fallback.');
        set({ isLoaded: true });
        return;
      }
      set({ areas: fresh, isLoaded: true });
    } catch (error) {
      log.error('Failed to fetch service areas; keeping cached value.', error);
      // Flip isLoaded anyway so consumers that gate on it (e.g. MapScreen's
      // shard-loading effects) proceed on the bootstrap fallback rather than
      // hanging indefinitely on transient Firestore errors.
      set({ isLoaded: true });
    }
  },

  findContaining: (latitude, longitude) => {
    for (const area of get().areas) {
      if (
        latitude >= area.bounds.minLat &&
        latitude <= area.bounds.maxLat &&
        longitude >= area.bounds.minLng &&
        longitude <= area.bounds.maxLng
      ) {
        return area;
      }
    }
    return null;
  },

  findContainingPoint: (latitude, longitude) => {
    const { areas, polygonsByAreaId } = get();
    for (const area of areas) {
      // Bounds are a cheap prefilter — skip the expensive polygon test if
      // the point can't possibly be inside.
      if (
        latitude < area.bounds.minLat ||
        latitude > area.bounds.maxLat ||
        longitude < area.bounds.minLng ||
        longitude > area.bounds.maxLng
      ) {
        continue;
      }
      const polygon = polygonsByAreaId[area.id];
      if (polygon) {
        if (pointInPolygon(latitude, longitude, polygon)) return area;
        continue;
      }
      // No polygon loaded — fall back to the rectangle match so the banner
      // doesn't misfire during the fetch window.
      return area;
    }
    return null;
  },

  findIntersecting: (south, west, north, east) => {
    return get().areas.filter(
      (area) =>
        area.bounds.minLat <= north &&
        area.bounds.maxLat >= south &&
        area.bounds.minLng <= east &&
        area.bounds.maxLng >= west
    );
  },

  ensurePolygonsLoaded: async (areaIds) => {
    const { polygonsByAreaId, polygonLoadFailures } = get();
    const missing = areaIds.filter((id) => !polygonsByAreaId[id] && !polygonLoadFailures.has(id));
    if (missing.length === 0) return;
    await Promise.all(
      missing.map(async (id) => {
        try {
          const res = await fetch(`/service-areas/${id}.geojson`);
          if (!res.ok) {
            if (res.status === 404) {
              set((s) => ({
                polygonLoadFailures: new Set(s.polygonLoadFailures).add(id),
              }));
              return;
            }
            throw new Error(`HTTP ${res.status}`);
          }
          const feature = (await res.json()) as ServiceAreaPolygon;
          set((s) => ({
            polygonsByAreaId: { ...s.polygonsByAreaId, [id]: feature },
          }));
        } catch (error) {
          log.warn(`Failed to load polygon for ${id}`, error);
          // Don't memo transient failures as 404s — let the next call retry.
        }
      })
    );
  },
}));

/**
 * Check if a coordinate is within any active service area.
 *
 * Reads from the serviceAreasStore cache (which is hydrated from Firestore
 * at app launch and falls back to a baked-in bootstrap value before the first
 * fetch returns). Stays synchronous so location-check UI doesn't have to
 * await on the critical path.
 */
export function isInServiceArea(
  latitude: number,
  longitude: number
): { isInArea: boolean; areaName: string | null } {
  const match = useServiceAreasStore.getState().findContaining(latitude, longitude);
  return match ? { isInArea: true, areaName: match.name } : { isInArea: false, areaName: null };
}
